<template>
  <canvas class="form-particle-trail" aria-hidden="true"></canvas>
</template>

<script>
import { getThemeColor, getThemeColorWithAlpha } from '@/utils/theme'

const GUIDE_EVENT = 'ui-form-field-guide'
const GUIDE_ARRIVED_EVENT = 'ui-form-guide-arrived'
const ACTION_GUIDE_EVENT = 'ui-form-action-guide'

export default {
  name: 'FormParticleTrail',
  props: {
    targetSelector: { type: String, default: '[data-ui-action="save"], [data-ui-action="create"]' }
  },
  data () {
    return {
      pageRoot: null,
      context: null,
      particles: [],
      journey: null,
      absorption: null,
      arrivalBurst: null,
      animationFrame: null,
      lastSource: null,
      lastLaunchAt: 0,
      inputTimer: null
    }
  },
  mounted () {
    this.pageRoot = this.$el.parentElement
    document.body.appendChild(this.$el)
    this.context = this.$el.getContext('2d')
    this.resizeCanvas()
    window.addEventListener('resize', this.resizeCanvas)
    window.addEventListener(GUIDE_EVENT, this.handleFieldGuide)
    window.addEventListener(ACTION_GUIDE_EVENT, this.handleActionGuide)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeCanvas)
    window.removeEventListener(GUIDE_EVENT, this.handleFieldGuide)
    window.removeEventListener(ACTION_GUIDE_EVENT, this.handleActionGuide)
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
    if (this.inputTimer) window.clearTimeout(this.inputTimer)
  },
  methods: {
    resizeCanvas () {
      const ratio = window.devicePixelRatio || 1
      this.$el.width = Math.round(window.innerWidth * ratio)
      this.$el.height = Math.round(window.innerHeight * ratio)
      this.$el.style.width = `${window.innerWidth}px`
      this.$el.style.height = `${window.innerHeight}px`
      if (this.context) this.context.setTransform(ratio, 0, 0, ratio, 0, 0)
    },
    handleFieldGuide (event) {
      const source = event.detail && event.detail.field
      if (!source || !event.detail.dirty || !source.isConnected) return
      const scope = event.detail.scope
      const isTextInput = source.matches('textarea, [contenteditable="true"], input:not([type]), input[type="text"], input[type="password"], input[type="search"], input[type="number"], input[type="email"], input[type="url"], input[type="tel"]')
      if (isTextInput) {
        if (this.inputTimer) window.clearTimeout(this.inputTimer)
        this.inputTimer = window.setTimeout(() => {
          this.inputTimer = null
          this.guideFromElement(source, scope)
        }, 650)
        return
      }
      this.guideFromElement(source, scope)
    },
    handleActionGuide (event) {
      const detail = event.detail || {}
      const sourceRect = detail.sourceRect || (detail.source && detail.source.isConnected ? detail.source.getBoundingClientRect() : null)
      const targetRect = detail.targetRect || (detail.target && detail.target.isConnected ? detail.target.getBoundingClientRect() : null)
      if (!sourceRect || !targetRect) return
      this.launch(sourceRect, targetRect, performance.now(), detail.scope, {
        arrivalEvent: detail.arrivalEvent,
        arrivalDetail: detail.arrivalDetail,
        arrivalEffect: detail.arrivalEffect
      })
    },
    guideFromElement (source, scope) {
      if (!source || !source.isConnected || !scope || !scope.isConnected) return
      const formItem = source.closest('.el-form-item, .route-design-popup-row')
      if (formItem && (formItem.classList.contains('is-error') || formItem.querySelector('.el-form-item__error'))) return
      if (source.getAttribute('aria-invalid') === 'true') return
      const now = performance.now()
      if (source === this.lastSource && now - this.lastLaunchAt < 420) return
      this.lastSource = source
      this.lastLaunchAt = now

      // 隐藏弹窗的按钮仍在 DOM 中，其矩形是 0，不能作为粒子终点。
      const surface = source.closest('.el-dialog, .el-drawer')
      const target = Array.from(scope.querySelectorAll(this.targetSelector)).find(button => {
        if (!button.isConnected || button.closest('.el-dialog, .el-drawer') !== surface) return false
        const rect = button.getBoundingClientRect()
        return rect.width > 0 && rect.height > 0 && window.getComputedStyle(button).visibility !== 'hidden'
      })
      if (!target) return
      const sourceTitle = formItem && formItem.querySelector('.el-form-item__label, .route-design-popup-row > span')
      this.launch((sourceTitle || source).getBoundingClientRect(), target.getBoundingClientRect(), now, scope)
    },
    launch (sourceRect, targetRect, startedAt, scope, options = {}) {
      if (![sourceRect, targetRect].every(rect => rect && rect.width > 0 && rect.height > 0 &&
        ['left', 'top', 'right', 'width', 'height'].every(key => Number.isFinite(rect[key])))) return
      const start = { x: sourceRect.right - 3, y: sourceRect.top + sourceRect.height / 2 }
      const end = { x: targetRect.left + targetRect.width / 2, y: targetRect.top + targetRect.height / 2 }
      const distance = Math.hypot(end.x - start.x, end.y - start.y)
      const lift = Math.min(150, Math.max(52, distance * 0.2))
      const control = {
        x: start.x + (end.x - start.x) * 0.5,
        y: Math.min(start.y, end.y) - lift
      }
      this.particles = []
      this.absorption = null
      this.arrivalBurst = null
      this.journey = {
        start,
        end,
        control,
        startedAt,
        duration: 900,
        lastSpawnAt: startedAt,
        targetRect,
        scope,
        options
      }
      if (!this.animationFrame) this.animationFrame = window.requestAnimationFrame(this.draw)
    },
    completeJourney (journey) {
      const options = journey.options || {}
      if (options.arrivalEffect === 'absorb') {
        this.startAbsorption(journey.targetRect)
      } else {
        this.startArrivalBurst(journey.targetRect)
      }
      const arrivalEvent = options.arrivalEvent || GUIDE_ARRIVED_EVENT
      window.dispatchEvent(new CustomEvent(arrivalEvent, {
        detail: { scope: journey.scope, ...(options.arrivalDetail || {}) }
      }))
    },
    startArrivalBurst (targetRect) {
      const startedAt = performance.now()
      const center = {
        x: targetRect.left + targetRect.width / 2,
        y: targetRect.top + targetRect.height / 2
      }
      this.arrivalBurst = {
        center,
        startedAt,
        duration: 560,
        particles: Array.from({ length: 30 }, (_, index) => ({
          angle: (Math.PI * 2 * index) / 30 + (Math.random() - 0.5) * 0.34,
          speed: 0.045 + Math.random() * 0.075,
          delay: Math.random() * 65,
          size: 0.9 + Math.random() * 2.2
        }))
      }
      if (!this.animationFrame) this.animationFrame = window.requestAnimationFrame(this.draw)
    },
    startAbsorption (targetRect) {
      const startedAt = performance.now()
      const center = {
        x: targetRect.left + targetRect.width / 2,
        y: targetRect.top + targetRect.height / 2
      }
      this.absorption = {
        center,
        startedAt,
        duration: 1250,
        particles: Array.from({ length: 46 }, () => ({
          angle: Math.random() * Math.PI * 2,
          radius: 30 + Math.random() * 42,
          delay: Math.random() * 260,
          travelDuration: 620 + Math.random() * 420,
          turns: (0.55 + Math.random() * 0.85) * (Math.random() > 0.5 ? 1 : -1),
          size: 0.9 + Math.random() * 2.4
        }))
      }
      if (!this.animationFrame) this.animationFrame = window.requestAnimationFrame(this.draw)
    },
    draw (now) {
      const ctx = this.context
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
      let active = false

      let orbPosition = null
      if (this.journey) {
        const progress = Math.min(1, (now - this.journey.startedAt) / this.journey.duration)
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2
        const inverse = 1 - eased
        orbPosition = {
          x: inverse * inverse * this.journey.start.x + 2 * inverse * eased * this.journey.control.x + eased * eased * this.journey.end.x,
          y: inverse * inverse * this.journey.start.y + 2 * inverse * eased * this.journey.control.y + eased * eased * this.journey.end.y
        }
        if (progress < 1) {
          active = true
          if (now - this.journey.lastSpawnAt >= 16) {
            this.journey.lastSpawnAt = now
            for (let index = 0; index < 3; index += 1) {
              this.particles.push({
                x: orbPosition.x + (Math.random() - 0.5) * 5,
                y: orbPosition.y + (Math.random() - 0.5) * 5,
                vx: (Math.random() - 0.5) * 0.045,
                vy: (Math.random() - 0.6) * 0.04,
                bornAt: now,
                lifetime: 420 + Math.random() * 520,
                size: 0.8 + Math.random() * 2.1
              })
            }
          }
        } else {
          const completedJourney = this.journey
          this.journey = null
          this.completeJourney(completedJourney)
        }
      }

      this.particles = this.particles.filter(particle => {
        const age = now - particle.bornAt
        if (age >= particle.lifetime) return false
        active = true
        const life = age / particle.lifetime
        const x = particle.x + particle.vx * age
        const y = particle.y + particle.vy * age
        ctx.beginPath()
        ctx.globalAlpha = Math.pow(1 - life, 1.6)
        ctx.fillStyle = getThemeColor(life < 0.4 ? 'particleCoreSoft' : 'particleWarm')
        ctx.shadowColor = getThemeColor('particleGlow')
        ctx.shadowBlur = 10
        ctx.arc(x, y, particle.size * (1 - life * 0.55), 0, Math.PI * 2)
        ctx.fill()
        return true
      })

      if (orbPosition && this.journey) {
        const halo = ctx.createRadialGradient(orbPosition.x, orbPosition.y, 1, orbPosition.x, orbPosition.y, 18)
        halo.addColorStop(0, getThemeColorWithAlpha('particleCore', 1))
        halo.addColorStop(0.18, getThemeColorWithAlpha('particleWarm', 0.95))
        halo.addColorStop(0.48, getThemeColorWithAlpha('particleGlow', 0.5))
        halo.addColorStop(1, getThemeColorWithAlpha('particleGlow', 0))
        ctx.globalAlpha = 1
        ctx.shadowBlur = 0
        ctx.fillStyle = halo
        ctx.beginPath()
        ctx.arc(orbPosition.x, orbPosition.y, 18, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = getThemeColor('particleCore')
        ctx.shadowColor = getThemeColor('particleGlow')
        ctx.shadowBlur = 18
        ctx.beginPath()
        ctx.arc(orbPosition.x, orbPosition.y, 3.8, 0, Math.PI * 2)
        ctx.fill()
      }

      if (this.absorption) {
        const elapsed = now - this.absorption.startedAt
        if (elapsed < this.absorption.duration) {
          active = true
          const { center } = this.absorption
          ctx.save()
          const portal = ctx.createRadialGradient(center.x, center.y, 1, center.x, center.y, 27)
          portal.addColorStop(0, getThemeColorWithAlpha('black', 0.78))
          portal.addColorStop(0.42, getThemeColorWithAlpha('backgroundCanvas', 0.58))
          portal.addColorStop(0.7, getThemeColorWithAlpha('particleGlow', 0.2))
          portal.addColorStop(1, getThemeColorWithAlpha('particleGlow', 0))
          ctx.fillStyle = portal
          ctx.beginPath()
          ctx.arc(center.x, center.y, 27, 0, Math.PI * 2)
          ctx.fill()
          ctx.globalCompositeOperation = 'lighter'
          this.absorption.particles.forEach(particle => {
            const progress = (elapsed - particle.delay) / particle.travelDuration
            if (progress < 0 || progress >= 1) return
            const eased = 1 - Math.pow(1 - progress, 2.25)
            const radius = particle.radius * (1 - eased)
            const angle = particle.angle + particle.turns * Math.PI * 2 * eased
            const x = center.x + Math.cos(angle) * radius
            const y = center.y + Math.sin(angle) * radius
            const alpha = Math.min(1, progress * 5) * Math.pow(1 - progress, 0.58)
            ctx.globalAlpha = alpha
            ctx.fillStyle = getThemeColor(progress > 0.72 ? 'particleCore' : 'particleWarm')
            ctx.shadowColor = getThemeColor('particleGlow')
            ctx.shadowBlur = 12
            ctx.beginPath()
            ctx.arc(x, y, particle.size * (1 - progress * 0.45), 0, Math.PI * 2)
            ctx.fill()
          })
          ctx.restore()
        } else {
          this.absorption = null
        }
      }

      if (this.arrivalBurst) {
        const elapsed = now - this.arrivalBurst.startedAt
        const progress = Math.min(1, elapsed / this.arrivalBurst.duration)
        if (progress < 1) {
          active = true
          const { center } = this.arrivalBurst
          const ringRadius = 6 + progress * 28
          ctx.save()
          ctx.globalAlpha = Math.pow(1 - progress, 1.8) * 0.8
          ctx.strokeStyle = getThemeColor('particleCore')
          ctx.shadowColor = getThemeColor('particleGlow')
          ctx.shadowBlur = 14
          ctx.lineWidth = Math.max(0.6, 2.2 * (1 - progress))
          ctx.beginPath()
          ctx.arc(center.x, center.y, ringRadius, 0, Math.PI * 2)
          ctx.stroke()
          this.arrivalBurst.particles.forEach(particle => {
            const particleProgress = (elapsed - particle.delay) / (this.arrivalBurst.duration - particle.delay)
            if (particleProgress < 0 || particleProgress >= 1) return
            const distance = particle.speed * Math.max(0, elapsed - particle.delay)
            const x = center.x + Math.cos(particle.angle) * distance
            const y = center.y + Math.sin(particle.angle) * distance + particleProgress * particleProgress * 5
            ctx.globalAlpha = Math.pow(1 - particleProgress, 1.45)
            ctx.fillStyle = getThemeColor(particleProgress < 0.42 ? 'particleCoreSoft' : 'particleWarm')
            ctx.beginPath()
            ctx.arc(x, y, particle.size * (1 - particleProgress * 0.55), 0, Math.PI * 2)
            ctx.fill()
          })
          ctx.restore()
        } else {
          this.arrivalBurst = null
        }
      }
      ctx.globalAlpha = 1
      ctx.shadowBlur = 0

      if (active) {
        this.animationFrame = window.requestAnimationFrame(this.draw)
      } else {
        this.animationFrame = null
        this.particles = []
        this.journey = null
        this.absorption = null
        this.arrivalBurst = null
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
      }
    }
  }
}
</script>

<style scoped>
.form-particle-trail {
  position: fixed;
  z-index: 2999;
  top: 0;
  left: 0;
  pointer-events: none;
}
</style>
